import React, { useEffect, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { FaBars } from 'react-icons/fa6';
import Logo from '../../icons/Logo';
import SettingsPanel from './components/SettingsPanel';
import useProfilePicture, { useAvatarChecked } from '../../../../hooks/account/useProfilePicture';
import userService from '../../../../services/userService';
import LogoLetter from '../../icons/LogoLetter';
import SideBarIcon from '../../icons/SideBarIcon';
import useSelectedCommunity from '../../../../hooks/community/useSelectedCommunity';
import useAuth from '../../../../hooks/account/useAuth';
import CommunityButton from './components/CommunityButton';
import DropdownPanel from './components/DropdownPanel';
import { RootState } from '../../../../redux/reducers';
import BusinessButton from './components/BusinessButton';
import useSelectedBusiness from '../../../../hooks/business/useSelectedBusiness';
import ChatAssistant from '../../../chat/ChatAssistant';
import TopBarActions from './components/TopBarActions';

interface ApplicationLayoutProps {
  children?: React.ReactNode;
  collapsed?: boolean;
}

export default function ApplicationLayout({
  children,
  collapsed: preCollapsed = false,
}: ApplicationLayoutProps): JSX.Element {
  const profilePicture = useProfilePicture();
  const avatarChecked = useAvatarChecked();
  const location = useLocation();
  const community = useSelectedCommunity();
  const business = useSelectedBusiness();
  const auth = useAuth();
  const { onboardingStep } = useSelector((state: RootState) => state.application);

  const [collapsed, setCollapsed] = useState(preCollapsed);
  const [closed, setClosed] = useState(true);
  const isBusiness = process.env.REACT_APP_APPLICATION_VERSION === 'BUSINESS';

  useEffect(() => {
    if (onboardingStep === undefined) return;
    setCollapsed(false);
  }, [onboardingStep]);

  useEffect(() => {
    if (!avatarChecked && !profilePicture) {
      userService.getProfilePicture();
    }
  }, [avatarChecked, profilePicture]);

  useEffect(() => setClosed(true), [location.pathname]);

  return (
    <div className="flex h-full min-h-screen w-full flex-col bg-canvas p-0 lg:flex-row lg:justify-between lg:gap-3 lg:p-3">
      {/* Warm-white sidebar */}
      <div
        className={classNames(
          'group top-3 z-50 flex h-fit w-full flex-shrink-0 flex-col rounded-card bg-surface py-4 shadow-sm transition-all duration-200 lg:sticky lg:max-h-[calc(100vh-1.5rem)]',
          {
            collapsed,
            open: !collapsed,
            'lg:w-14': collapsed,
            'items-start px-3 pr-4 lg:w-[260px]': !collapsed,
          },
        )}>
        <div className="flex w-full items-center justify-between px-1">
          <Link to="/" className="w-fit">
            {collapsed ? (
              <>
                <LogoLetter className="hidden min-h-[48px] lg:block" />
                <Logo className="lg:hidden" />
              </>
            ) : (
              <Logo className="lg:min-h-[64px] lg:min-w-[200px]" />
            )}
          </Link>
          <FaBars
            className="h-6 w-6 cursor-pointer text-primary-900 lg:hidden"
            onClick={() => setClosed(!closed)}
          />
        </div>
        <div
          className={classNames(
            'no-scrollbar flex w-full flex-1 flex-col overflow-hidden transition-all lg:max-h-[calc(100vh-180px)] lg:group-[.collapsed]:overflow-visible lg:group-[.open]:overflow-y-auto',
            {
              'max-h-[0px] duration-500 ease-[cubic-bezier(0,1,0,1)]': closed,
              'max-h-[2000px] duration-1000 ease-[ease-in-out]': !closed,
            },
          )}>
          {(community || business) && (
            <div className="mt-3 flex w-full flex-col items-center justify-center gap-1 border-t border-secondary-200 pt-2">
              <DropdownPanel />
            </div>
          )}
          <div className="mt-auto flex w-full flex-col items-center justify-center gap-1 border-t border-secondary-200 pt-3">
            <SettingsPanel />
          </div>
        </div>
      </div>

      {/* Main column: top bar + off-white content card */}
      <div
        className={classNames('flex min-h-[calc(100vh-72px)] w-full flex-col gap-3', {
          'lg:w-[calc(100%-272px)]': !collapsed,
          'lg:w-[calc(100%-68px)]': collapsed,
        })}>
        <div
          className={classNames(
            'sticky top-0 z-[50] flex w-full items-center justify-between rounded-card bg-surface px-4 py-3 shadow-sm',
            { 'opacity-80 blur-sm': onboardingStep !== undefined },
          )}>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setCollapsed(!collapsed)}
              className="hidden h-fit w-fit flex-shrink-0 cursor-pointer items-center justify-center rounded-[8px] bg-secondary-200 p-2 hover:bg-opacity-80 lg:flex">
              <SideBarIcon className="h-5 w-5" />
            </button>
            {isBusiness ? <BusinessButton /> : <CommunityButton />}
          </div>
          {location.pathname !== '/profile' && auth && <TopBarActions />}
        </div>

        <div className="flex h-full w-full flex-1 flex-col rounded-card bg-surface px-6 py-5 shadow-sm">
          {children || <Outlet />}
        </div>
      </div>
      <ChatAssistant />
    </div>
  );
}
